import React from 'react';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { MapMarker, Calendar, AddCircle } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTranslation } from '@shared/core';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { useEventLocaleFormatter } from '@shared/core/localization/hooks/useEventLocaleFormatter';

type AccommodationsHeaderProps = Readonly<{
  onEditVenue: () => void;
  onAddVenue: () => void;
  onEditDates: () => void;
}>;

/**
 * Builds a venue display string from name + location parts.
 * Figma shows: "Roxbury Barn & Estate, Los Angeles, Ca..."
 */
const buildVenueDisplayText = (venue: { name?: string; city?: string | null; stateProvince?: string | null }) => {
  const parts = [venue.name, venue.city, venue.stateProvince].filter(Boolean);
  return parts.join(', ');
};

const FOCUS_VISIBLE_STYLES = { outline: '2px solid currentColor' } as const;

const useStayDateRange = () => {
  const { travelSettings } = useAccommodationsContext();
  const { eventInfo } = useEventInfo();
  const { formatDateRange } = useEventLocaleFormatter();

  const checkInStr = travelSettings?.checkInDate;
  const checkOutStr = travelSettings?.checkOutDate;

  // Priority 1: admin-set check-in/check-out from travelSettings
  if (checkInStr && checkOutStr) {
    return formatDateRange(checkInStr, checkOutStr);
  }

  // Priority 2: calculate from event date (±1 day)
  const eventDateStr = eventInfo?.finalizedDate?.dateString;
  const { checkIn, checkOut } = calculateStayDates(eventDateStr);
  return formatDateRange(checkIn.date, checkOut.date);
};

const AccommodationsHeader: React.FC<AccommodationsHeaderProps> = ({ onEditVenue, onAddVenue, onEditDates }) => {
  const { venueData } = useAccommodationsContext();
  const { tq } = useTranslation('accommodationsAdmin');
  const stayDateRange = useStayDateRange();

  const venueDisplayText = venueData?.hasVenue && venueData.name ? buildVenueDisplayText(venueData) : null;

  return (
    <Flex
      alignItems="center"
      gap={4}
      width="100%"
      paddingX={{ _: 6, sm2: 8 }}
    >
      {venueData?.hasVenue ? (
        <Flex
          as="button"
          onClick={onEditVenue}
          cursor="pointer"
          border="none"
          background="none"
          padding={0}
          alignItems="center"
          gap={1}
          overflow="hidden"
          flex={1}
          minWidth={0}
          aria-label={venueDisplayText || 'Edit venue'}
          _hover={{ opacity: 0.7 }}
          _focusVisible={FOCUS_VISIBLE_STYLES}
        >
          <Flex
            flexShrink={0}
            alignItems="center"
            justifyContent="center"
            width={6}
            height={6}
          >
            <MapMarker size={24} />
          </Flex>
          <TextV2
            fontSize={pxToRem(13)}
            lineHeight={1.4}
            letterSpacing={pxToRem(-0.0325)}
            color="mono14"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {venueDisplayText}
          </TextV2>
        </Flex>
      ) : (
        <ButtonV2
          variant="ghost"
          intent="neutral"
          shape="rounded"
          startIcon={<AddCircle size={16} />}
          onClick={onAddVenue}
          flex={1}
          justifyContent="flex-start"
          paddingX={2}
          fontSize={pxToRem(13)}
        >
          {tq`Add Venue`}
        </ButtonV2>
      )}
      {stayDateRange && (
        <Flex
          as="button"
          onClick={onEditDates}
          cursor="pointer"
          border="none"
          background="none"
          padding={0}
          alignItems="center"
          alignSelf="stretch"
          aria-label={stayDateRange}
          _hover={{ opacity: 0.7 }}
          _focusVisible={FOCUS_VISIBLE_STYLES}
        >
          <Flex
            alignItems="center"
            gap={1}
            overflow="hidden"
            flexShrink={0}
          >
            <Flex
              flexShrink={0}
              alignItems="center"
              justifyContent="center"
              width={6}
              height={6}
            >
              <Calendar size={24} />
            </Flex>
            <TextV2
              fontSize={pxToRem(13)}
              lineHeight={1.4}
              letterSpacing={pxToRem(-0.0325)}
              color="mono14"
              whiteSpace="nowrap"
            >
              {stayDateRange}
            </TextV2>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default AccommodationsHeader;
